'use client'

import { AnimatePresence, motion } from 'motion/react'
import React, { useState, useEffect } from 'react'
import { Layout422Block as Layout422BlockProps, Media as MediaProps } from '@/payload-types'
import { Media } from '@/components/Media'
import { CMSLink } from '@/components/Link'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'

const slideVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    y: 50,
  },
  visible: {
    opacity: 1,
    height: 'auto',
    y: 0,
  },
}

export const Layout422Block: React.FC<Layout422BlockProps> = ({
  tagline,
  heading,
  description,
  features,
  theme,
  icon
}) => {
  const [hoveredFeatureIdx, setHoveredFeatureIdx] = useState<number | null>(null)
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null)
  const [openDialogIndex, setOpenDialogIndex] = useState<number | null>(null)  // Handle escape key globally
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openDialogIndex !== null) {
        setOpenDialogIndex(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDialogIndex]);

  return (
    <section data-theme={theme} className="bg-background py-16 md:py-24 lg:py-28">
      <div className="container">
        <div className="mx-auto mb-12 w-full max-w-lg text-center md:mb-18 lg:mb-20">          {icon && (
            <div className="mb-4 flex items-center justify-center">
              <img
                src={(icon as MediaProps)?.url!}
                alt={(icon as MediaProps)?.alt || ''}
                className="h-12 w-12 rounded-full object-cover"
              />
            </div>
          )}
          <p className="mb-3 font-semibold md:mb-4">{tagline}</p>
          <h2 className="mb-5 text-5xl font-bold md:mb-6 md:text-7xl lg:text-8xl">{heading}</h2>
          <p className="md:text-md">{description}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">          {features?.map((feature, index) => (
            <Dialog 
              key={index} 
              open={openDialogIndex === index} 
              onOpenChange={(open) => {
                if (open) {
                  setOpenDialogIndex(index);
                  setSelectedFeature(index);
                } else {
                  setOpenDialogIndex(null);
                }
              }}
            >
              <DialogTrigger asChild>
                <div
                  className="relative flex w-full flex-col overflow-hidden lg:h-full transition-all duration-200 hover:scale-[1.02] cursor-pointer !rounded-sm"
                  onMouseEnter={() => setHoveredFeatureIdx(index)}
                  onMouseLeave={() => setHoveredFeatureIdx(null)}
                  onClick={() => setOpenDialogIndex(index)}
                >
                  {' '}
                  <div className="group absolute inset-0 flex size-full flex-col items-center justify-center self-start">                    <img
                      src={(feature.image as MediaProps)?.url!}
                      alt={(feature.image as MediaProps)?.alt || ''}
                      className="size-full object-cover"
                    />
                  </div>
                  <div className="group relative flex h-full min-h-[50vh] flex-col justify-end p-6 md:p-8 lg:p-10">
                    {/* " */}
                    <div
                      className={
                        theme === 'light'
                          ? 'z-10 bg-white/60 p-4 rounded-sm backdrop-blur-sm'
                          : 'z-10 bg-black/60 p-4 rounded-sm backdrop-blur-sm'
                      }
                    >
                      <p className="mb-2 font-semibold text-text-alternative">{feature.tagline}</p>
                      <h3 className="font-bold leading-[1.2] text-text-alternative md:text-xl lg:text-2xl">
                        {feature.heading}
                      </h3>
                      <div className="lg:hidden">
                        {/* <p className="mt-5 text-text-alternative md:mt-6">{feature.description}</p> */}
                        <div className="mt-6 md:mt-8">
                          {feature.buttons?.map((button, idx) => (
                            <CMSLink key={idx} {...button.link} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <AnimatePresence>
                      {hoveredFeatureIdx === index && (
                        <motion.div
                          className="z-10 hidden lg:block w-full"
                          variants={slideVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          {/* <p className="mt-5 text-text-alternative md:mt-6">{feature.description}</p> */}
                          <div className="mt-6 md:mt-8">
                            {feature.buttons?.map((button, idx) => (
                              <CMSLink key={idx} {...button.link} />
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </DialogTrigger>              <DialogContent className="max-w-xxl !rounded-sm">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="overflow-hidden rounded-sm">
                    <Media resource={feature.image as MediaProps} className="w-full h-auto max-h-[50vh] md:max-h-none object-cover" />
                  </div>
                  <div className="flex flex-col">
                    <DialogTitle className="text-2xl mb-4 font-bold">{feature.heading}</DialogTitle>

                    <p className="mb-2 font-semibold">{feature.tagline}</p>
                    <p className="text-lg">{feature.description}</p>
                    <div className="mt-6 flex flex-wrap gap-4">
                      {feature.buttons?.map((button, idx) => (
                        <CMSLink key={idx} {...button.link} />
                      ))}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </div>
    </section>
  )
}
